import EntityTable from '@shared/components/tables/entity-table/entity-table.vue';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider.vue';
import { Component, Prop } from 'vue-property-decorator';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { mixins } from 'vue-class-component';
import AbstractTable from '@shared/components/tables/abstract-table/abstract-table';
import StdFilter from '@shared/components/filters/std-filter/std-filter.vue';
import ContactRelationCategoryTable from '@contacts/components/tables/contact-relation-category-table/contact-relation-category-table.vue';
import PriceCategoryTable from '@contacts/components/tables/price-category-table/price-category-table.vue';
import ContactTagTable from '@contacts/components/tables/contact-tag-table/contact-tag-table.vue';
import { Contact } from '@contacts/interfaces/contact';
import router from '@app/router';
import ContactForm from '@contacts/components/forms/contact-form/contact-form.vue';
import DrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import AuthorizationHelper from '@auth/services/authorization.helper';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider';
import ModulesHelper from '@core/services/modules.helper';
import ExtendedFilter from '@shared/components/filters/extended-filter/extended-filter.vue';
import CustomFilterSelector from '@shared/components/filters/custom-filter-selector/custom-filter-selector.vue';
import SubscriptionForm from '@subscriptions/components/forms/subscription-form/subscription-form.vue';
import MembershipForm from '@subscriptions/components/forms/admin/membership-form.vue';
import CouponForm from '@accounting/components/forms/coupon-form/coupon-form.vue';
import BulkMoveToTrashModal from '@shared/components/modals/bulk-move-to-trash-modal/bulk-move-to-trash-modal.vue';
import SendMessageUsingTemplateMixin from '@shared/components/tables/abstract-table/send-message-using-template-mixing';
import BulkRecoverModal from '@shared/components/modals/bulk-recover-modal/bulk-recover-modal.vue';
import { Membership } from '@subscriptions/interfaces/subscription';
import moment from 'moment';
import ContentWrapper from '@shared/layouts/wrappers/content-wrapper.vue';
import { setProp } from '@shared/helpers/operators';
import AccessControlBadgeForm from '@access-control/components/access-control-badge-form.vue';
import { AccessControlBadge } from '@access-control/interfaces/access-control-badge';
import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import BpcRecreditSubscriptionModal from '@subscriptions/components/modals/recredit-subscription-modal.vue';
import MergeContactsModal from '@contacts/components/merge-contacts/merge-contacts-modal.vue';

@Component({
  name: 'bpc-contact-table',
  components: {
    MergeContactsModal,
    'bpc-entity-select': EntitySelect,
    'bpc-access-control-badge-form': AccessControlBadgeForm,
    'bpc-entity-table-provider': EntityTableProvider,
    'bpc-entity-table': EntityTable,
    'bpc-page-title': BpcPageTitle,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-contact-form': ContactForm,
    'bpc-std-filter': StdFilter,
    'bpc-contact-relation-category-table': ContactRelationCategoryTable,
    'bpc-price-category-table': PriceCategoryTable,
    'bpc-contact-tag-table': ContactTagTable,
    'bpc-drawer-modal': DrawerModal,
    'bpc-std-form': StdForm,
    'bpc-extended-filter': ExtendedFilter,
    'bpc-custom-filter-selector': CustomFilterSelector,
    'bpc-subscription-form': SubscriptionForm,
    'bpc-membership-form': MembershipForm,
    'bpc-coupon-form': CouponForm,
    'bpc-bulk-move-to-trash-modal': BulkMoveToTrashModal,
    'bpc-bulk-recover-modal': BulkRecoverModal,
    'bpc-content-wrapper': ContentWrapper,
    BpcRecreditSubscriptionModal,
  },
})
export default class ContactTable extends mixins(AbstractTable, SendMessageUsingTemplateMixin) {
  declare $refs: AbstractTable['$refs'] & {
    stdMessageForm: InstanceType<typeof StdForm> & {
      formModel: any;
      saveEntity(): void;
      formProvider: EntityFormProvider;
    };
    stdSubscriptionForm: InstanceType<typeof SubscriptionForm> & {
      saveEntity(): void;
      setSelectedContacts(contacts: any): void;
    };
    stdCouponForm: InstanceType<typeof CouponForm> & {
      saveEntity(): void;
      setSelectedContacts(contacts: any): void;
    };
    bulkAddToTeamOrGroupForm: InstanceType<typeof StdForm> & {
      formModel: any;
      saveEntity(): void;
      formProvider: EntityFormProvider;
    };
    stdMembershipForm: InstanceType<typeof MembershipForm> & {
      saveEntity(): void;
      setSelectedContacts(contacts: any): void;
    };
  };

  constructor() {
    super();
  }

  @Prop({ type: String, default: 'table' })
  declare readonly displayConfigName: string;
  @Prop({ type: String, default: 'default-tabs' })
  declare readonly filterConfigName: string;
  @Prop(String) declare readonly title: string;
  @Prop(String) declare readonly subtitle: string;
  @Prop({ type: String, default: null }) readonly configFileNameForExport: string;

  @Prop({ type: String, default: 'bulk-edit' }) bulkEditConfigName: string;

  @Prop(String) declare readonly modalShowTitle: string;
  @Prop(String) declare readonly modalCreateTitle: string;
  @Prop(String) declare readonly modalEditTitle: string;
  @Prop(String) readonly fetchPath: string;

  public entity: Contact = null;
  public entityName = 'contact';
  public pluralEntityName = 'contacts';
  recreditSubscriptionId = null;
  showRecreditForm = false;

  getEntities() {
    this.$refs.provider.getEntities();
  }
  onShowRecreditModal(subscriptionId) {
    this.recreditSubscriptionId = subscriptionId;
    this.showRecreditForm = true;
  }

  get heading() {
    console.log(this.$route.meta['contactType']);
    return this.$route.meta['contactType']
      ? this.$t('global.sidebar_menu.main_menu.league.' + this.$route.meta['contactType'] + 's')
      : this.title ?? this.$t('global.models.contact.heading');
  }

  get subheading() {
    return this.subtitle ?? this.$t('global.models.contact.subheading');
  }

  getModalTitle() {
    if (this.disabledForm && this.modalShowTitle) {
      return this.$t(this.modalShowTitle).toString();
    } else if (this.disabledForm) {
      const title = this.$t('global.models.' + this.entityName + '.name').toString();
      return title.charAt(0).toUpperCase() + title.slice(1); // capitalize
    } else if (this.entity?.id && this.modalEditTitle) {
      return this.$t(this.modalEditTitle).toString();
    } else if (this.entity && this.entity.id) {
      return (
        this.$t('global.modals.edit') + ' ' + this.$t('global.models.' + this.entityName + '.name')
      );
    } else if (this.modalCreateTitle) {
      return this.$t(this.modalCreateTitle).toString();
    } else {
      return (
        this.$t('global.modals.create') +
        ' ' +
        this.$t('global.models.' + this.entityName + '.name')
      );
    }
  }

  get contacCardEnabled(): boolean {
    return !!this.$store.getters['centerSettings/enableContactCard'];
  }

  // Related with merge contacts
  mergeContactsButtonLabel: string = 'global.merge_contacts_button_label';
  showMergeContactsModal: boolean = false;
  isMergingContacts: boolean = false;
  get selectedContactId(): number {
    return this.entity?.id;
  }
  openMergeContactsModal(): void {
    this.showMergeContactsModal = true;
  }
  closeMergeContactsModal(): void {
    this.showMergeContactsModal = false;
  }

  onContactsMerged() {
    this.i++;
    this.$refs.provider.getEntities();
  }

  mounted() {
    const templatesRequest = [this.getAllowedTemplatesForSendMessage()];

    Promise.all(templatesRequest).then(res => {
      this.updateRowActions();
      this.updatePageActions();
    });

    this.$root.$on('saved-on-change-from-table', val => {
      //this.$refs.provider.getEntities();
    });

    this.$root.$on('selected-apply-bulk-edit', (option, entity) => {
      if (entity === 'contact-tag') {
        setProp(this.$refs.stdBulkForm.formModel, 'apply_tags', option.value);
        if (option.value === 'clear_field') {
          setProp(this.$refs.stdBulkForm.formModel, 'contactTags', []);
        }
      } else if (entity === 'resource') {
        setProp(this.$refs.stdBulkForm.formModel, 'apply_resources', option.value);
        if (option.value === 'clear_field') {
          setProp(this.$refs.stdBulkForm.formModel, 'resources', []);
        }
      }
    });
  }

  destroyed() {
    this.$root.$off('selected-apply-bulk-edit');
    this.$off('saved-on-change-from-table');
  }
  /**
   * Contact actions
   */
  public customPageActions = {
    bulkActions: {
      activated: false,
      type: 'dropdown',
      icon: 'cubes',
      variant: 'info',
      text: 'bulk_actions',
      show: false,
      event: '',
      selectElements: true,
      tag: 'bulk',
      permissions: [],
      children: {
        creationHead: {
          activated: false,
          type: 'head',
          text: 'creation_head',
          show: true,
          withoutDivider: true,
          permissions: [],
        },
        bulkSubscription: {
          activated: false,
          type: 'dropdown-item',
          text: 'create_subscription',
          show: true,
          event: 'bulk-subscription',
          permissions: ['bulk_subscription'],
        },
        bulkCoupon: {
          activated: false,
          type: 'dropdown-item',
          text: 'create_coupons',
          show: true,
          event: 'bulk-coupon',
          permissions: ['bulk_coupon'],
        },
        bulkMembership: {
          activated: false,
          type: 'dropdown-item',
          text: 'create_memberships',
          show: true,
          event: 'bulk-membership',
          permissions: ['bulk_subscription'],
        },
        bulkAddToTeamOrGroup: {
          activated: false,
          type: 'dropdown-item',
          text: 'bulk_add_to_team_or_group',
          show: true,
          event: 'bulk-add-to-team-or-group',
          // permissions: ['bulk_add_to_team_or_group'],
          permissions: [],
        },
      },
    },
    bulkSendMessageUsingTemplate: {
      activated: false,
      type: 'action',
      icon: 'envelope',
      variant: 'info',
      text: 'send_message_using_template',
      show: true,
      event: 'bulk-send-message-using-template',
      selectElements: false,
      tag: 'right',
      permissions: ['bulk_message'],
    },
    bulkEditTPPWB: {
      activated: false,
      type: 'action',
      icon: 'edit',
      variant: 'info',
      text: 'bulk_edit_tppwb',
      show: true,
      event: 'bulk-edit-tppwb',
      selectElements: true,
      tag: 'right',
      permissions: ['bulk_edit'],
    },
    bulkEditTPPWBTennis: {
      activated: false,
      type: 'action',
      icon: 'edit',
      variant: 'info',
      text: 'bulk_edit_tppwb',
      show: true,
      event: 'bulk-edit-tppwb-tennis',
      selectElements: true,
      tag: 'right',
      permissions: ['bulk_edit'],
    },
    bulkEditTPPWBPadel: {
      activated: false,
      type: 'action',
      icon: 'edit',
      variant: 'info',
      text: 'bulk_edit_tppwb',
      show: true,
      event: 'bulk-edit-tppwb-padel',
      selectElements: true,
      tag: 'right',
      permissions: ['bulk_edit'],
    },
    // bulkAddToTeamOrGroup: {
    //   activated: false,
    //   type: 'action',
    //   icon: 'users',
    //   variant: 'info',
    //   text: 'bulk_add_to_team_or_group',
    //   show: true,
    //   event: 'bulk-add-to-team-or-group',
    //   selectElements: true,
    //   tag: 'right',
    //   permissions: ['bulk_add_to_team_or_group'],
    // },
  };

  onFormSave(next: string = '') {
    this.nextAction = next;
    this.$refs.stdForm.formModel.teamsAndGroups = null;
    this.$refs.stdForm.formModel.family = null;
    this.$refs.stdForm.saveEntity();
  }

  extendUpdatePageActions() {
    if (this.config) {
      const activatedPageActions = this.config?.data?.activatedPageActions || [];
      for (const activatedPageAction of activatedPageActions) {
        if (this.customPageActions[activatedPageAction]) {
          this.customPageActions[activatedPageAction].activated = true;
        }
      }
    }

    this.customPageActions.bulkSendMessageUsingTemplate.show =
      this.tableIsNotFilteredByTrashedElements() &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME);

    this.customPageActions.bulkActions.children.bulkAddToTeamOrGroup.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$modulesService.hasModuleActivated(ModulesHelper.CONTACTS_MODULE_NAME) &&
      this.tenantType === 'club';

    this.customPageActions.bulkActions.children.bulkMembership.show = this.tenantType === 'club';

    this.customPageActions.bulkActions.children.creationHead.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      (this.$modulesService.hasModuleActivated(ModulesHelper.SUBSCRIPTIONS_MODULE_NAME) ||
        this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME));

    this.customPageActions.bulkActions.children.bulkSubscription.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$modulesService.hasModuleActivated(ModulesHelper.SUBSCRIPTIONS_MODULE_NAME);

    this.customPageActions.bulkActions.children.bulkCoupon.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME);

    this.customPageActions.bulkActions.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      (this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME) ||
        this.$modulesService.hasModuleActivated(ModulesHelper.SUBSCRIPTIONS_MODULE_NAME) ||
        this.$modulesService.hasModuleActivated(ModulesHelper.ACCOUNTING_MODULE_NAME));

    // Check Permissions
    const activatedPageActions = this.config?.data?.activatedPageActions || [];

    for (const activatedPageAction of activatedPageActions) {
      if (
        this.customPageActions[activatedPageAction] &&
        this.customPageActions[activatedPageAction]['permissions'] &&
        this.customPageActions[activatedPageAction]['permissions'].length != 0
      ) {
        const permissions = [];
        this.customPageActions[activatedPageAction]['permissions'].forEach(permissionName => {
          permissions.push(
            {
              name: AuthorizationHelper.getPermissionName(
                permissionName,
                (this.permissionEntityNameToUse
                  ? this.permissionEntityNameToUse
                  : this.pluralEntityName
                ).replace('-', '_'),
              ),
            },
            {
              name: AuthorizationHelper.getPermissionName(
                permissionName,
                (this.permissionEntityNameToUse
                  ? this.permissionEntityNameToUse
                  : this.pluralEntityName
                ).replace('-', '_'),
                true,
              ),
            },
          );
        });
        this.customPageActions[activatedPageAction][
          'activated'
        ] = this.$authorizationService.hasPermissions(permissions);
      }
    }

    // FIXME ONLY FOR CLUBS
    if (this.$store.getters['centerSettings/centerType'] === 'club') {
      this.customPageActions.bulkActions.children.bulkSubscription.show = false;
      this.customPageActions.bulkActions.children.bulkCoupon.show = false;
    }
  }

  get pageActions() {
    return {
      ...this.customPageActions,
      ...this.defaultPageActions,
      ...this.settingsPageAction,
    };
  }

  get tenantType(): string {
    return this.$store.getters['centerSettings/centerSettings']['center_type'] || 'center';
  }

  customOnPageAction($event: string) {
    switch ($event) {
      case 'settings':
        this.onContactSetting();
        break;
      case 'bulk-send-message-using-template':
        this.onBulkSendMessageUsingTemplateToContacts(this.selection);
        break;
      case 'bulk-add-to-team-or-group':
        this.onBulkAddToTeamOrGroup(this.selection);
        break;
      case 'bulk-subscription':
        this.onBulkSubscription();
        break;
      case 'bulk-coupon':
        this.onBulkCoupon();
        break;
      case 'bulk-membership':
        this.onBulkMembership();
        break;
      case 'bulk-edit-tppwb':
        this.bulkEditConfigName = 'bulk-edit-tppwb';
        this.onBulkEdit();
        break;
      case 'bulk-edit-tppwb-tennis':
        this.bulkEditConfigName = 'bulk-edit-tppwb-tennis';
        this.onBulkEdit();
        break;
      case 'bulk-edit-tppwb-padel':
        this.bulkEditConfigName = 'bulk-edit-tppwb-padel';
        this.onBulkEdit();
        break;
    }
    console.log('Page Title event >>> ' + $event);
  }

  showBulkAddToTeamOrGroupModal: boolean = false;
  selectedContactIds: number[] = [];
  selectedContactsToAddTeamOrGroup: number[] = [];

  get bulkAddToTeamOrGroupSavePath(): string {
    const basePath = this.$bpcApiRouteHelper.getEntityPath('contact-relations');
    const path = `${basePath}/bulk-add-to-team-or-group`;
    return path;
  }

  async onBulkAddToTeamOrGroup(selection: number[]) {
    this.selectedContactIds = selection;
    this.onShowBulkAddToTeamOrGroupModal();
  }

  async onBulkMembershipSaved() {
    this.$refs.provider.getEntities();
    this.onCloseMembershipModal();
  }

  onBulkAddToTeamOrGroupSaved() {
    console.log('onBulkAddToTeamOrGroupSaved');
    this.closeBulkAddToTeamOrGroupModal();
    this.$refs.provider.getEntities();
  }
  onBulkAddtoTeamOrGroupModelLoaded() {
    console.log('onBulkAddtoTeamOrGroupModelLoaded');
    this.selectedContactsToAddTeamOrGroup = [...this.selectedContactIds];
  }

  setContactsToAddIntoTeamOrGroup(entityCopy) {
    this.$set(entityCopy, 'relatedContacts', this.selectedContactsToAddTeamOrGroup);
    return entityCopy;
  }

  closeBulkAddToTeamOrGroupModal() {
    this.showBulkAddToTeamOrGroupModal = false;
  }
  saveBulkAddToTeamOrGroup() {
    // SOme logic here
    // const newFormModel = this.$refs.bulkAddToTeamOrGroupForm.formModel;
    // newFormModel.relatedContacts = this.selectedContactIds;
    this.$refs.bulkAddToTeamOrGroupForm.saveEntity();
  }

  onShowBulkAddToTeamOrGroupModal() {
    this.showBulkAddToTeamOrGroupModal = true;
  }

  public customRowActions = {
    sendMessageUsingTemplate: {
      activated: true,
      type: 'action',
      icon: 'envelope',
      show: true,
      event: 'send-message-using-template',
      text: 'send_message_using_template',
      permissions: ['send'],
    },
    downloadCard: {
      activated: true,
      type: 'action',
      icon: 'id-card',
      show: true,
      event: 'download-card',
      text: 'download-card',
      permissions: ['download'],
    },
  };

  onRowAction($event: { event: string; item: any }) {
    switch ($event.event) {
      case 'delete':
        this.onDelete($event.item);
        break;
      case 'soft-delete':
        this.onSoftDelete($event.item);
        break;
      case 'recover':
        this.onRecover($event.item);
        break;
      case 'edit':
        this.onEdit($event.item);
        break;
      case 'show':
        this.onShow($event.item);
        break;
      case 'select':
        this.onSelect($event.item);
        break;
      case 'send-message-using-template':
        this.onSendMessageUsingTemplateToContact($event.item.item);
        break;
      case 'download-card':
        this.downloadCard($event?.item?.item?.id);
        break;
    }
    console.log('Row action event >>> ' + $event);
  }

  extendUpdateRowActions(entity: any) {
    entity.actions.sendMessageUsingTemplate.show =
      this.tableIsNotFilteredByTrashedElements() &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME);

    entity.actions.downloadCard.show =
      entity.status !== 'cancelled' &&
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.contacCardEnabled;
  }

  setEntityAttributesAfterSave() {
    const newEntity = {} as Contact;
    Object.keys(this.entity).forEach(key => {
      this.$set(newEntity, key, null);
    });

    this.$set(newEntity, 'status', this.entity.status);
    this.$set(newEntity, 'type', this.entity.type);
    this.$set(this.$refs.stdForm, 'formModel', newEntity);

    this.showForm = true;
    this.showDrawerSection = true;
  }

  onNew() {
    this.$bpcStorage.clear('contact-relations');
    this.entity = null;
    this.showForm = true;
    this.showDrawerSection = true;
    this.disabledForm = false;
  }

  onShow(e) {
    console.log('clickOnEdit', e);
    const rowEntity = e.item;
    router.push({
      name: 'admin-contact-show',
      params: {
        contact_id: rowEntity.id,
        contact: rowEntity,
        withMessages: 'false',
        originShowContact: 'true',
      },
    });
  }

  deselectAllContacts() {
    this.$refs.provider.items.forEach(item => {
      this.$set(item, 'checked', false);
    });
    this.selection = [];
  }

  /**
   * Contact settings
   */
  public showDrawerSettingSection: boolean = false;
  public showSettingForm: boolean = false;
  refreshTags = 0;
  refreshCategories = 0;
  refreshRelations = 0;
  public settingsPageAction = {
    settings: {
      activated: false,
      type: 'action',
      icon: 'cog',
      variant: 'secondary',
      text: null,
      show: true,
      event: 'settings',
      selectElements: false,
      tag: 'right',
    },
  };

  onContactSetting() {
    // this.crudEntitySetting = entity;
    this.showSettingForm = true;
    this.showDrawerSettingSection = true;
  }

  onSettingFormClose() {
    this.showSettingForm = false;
    this.showDrawerSettingSection = false;
  }

  onRefreshTags() {
    this.refreshTags++;
  }

  onRefreshCategories() {
    this.refreshCategories++;
  }

  onRefreshRelations() {
    this.refreshRelations++;
  }

  /**
   * Bulk create Subscription
   */
  showBulkSubscriptionDrawer: boolean = false;
  keyBulkSubscription: number = 0;
  savingSubscription: boolean = false;
  get disabledSubscriptionForm(): boolean {
    return !this.$authorizationService.hasPermission('manage_contacts');
  }

  onBulkSubscription() {
    this.showBulkSubscriptionDrawer = true;
  }

  onBulkSubscriptionDrawerModelLoaded() {
    setTimeout(() => {
      this.$refs.stdSubscriptionForm.setSelectedContacts(
        this.$refs.provider.items.filter(item => item['checked'] === true),
      );
    });
  }

  onCloseBulkSubscription() {
    this.showBulkSubscriptionDrawer = false;
    this.deselectAllContacts();
    this.keyBulkSubscription++;
  }

  onCreateBulkSubscription() {
    // this.savingSubscription = true;
    this.$refs.stdSubscriptionForm.saveEntity();
  }

  onCreatedBulkSubscription() {
    this.savingSubscription = false;
    this.showBulkSubscriptionDrawer = false;
    this.deselectAllContacts();
    this.keyBulkSubscription++;
  }

  onSavingSubscription(event) {
    this.savingSubscription = event;
  }

  /**
   * Bulk create Coupon
   */
  showBulkCouponDrawer: boolean = false;
  keyBulkCoupon: number = 0;
  savingCoupon: boolean = false;
  get disabledCouponForm(): boolean {
    return !this.$authorizationService.hasPermission('manage_contacts');
  }

  onBulkCoupon() {
    this.showBulkCouponDrawer = true;
  }

  onBulkCouponDrawerModelLoaded() {
    setTimeout(() => {
      this.$refs.stdCouponForm.setSelectedContacts(
        this.$refs.provider.items.filter(item => item['checked'] === true),
      );
    });
  }

  onCloseBulkCoupon() {
    this.showBulkCouponDrawer = false;
    this.deselectAllContacts();
    this.keyBulkCoupon++;
  }

  onCreateBulkCoupon() {
    // this.savingCoupon = true;
    this.$refs.stdCouponForm.saveEntity();
  }

  onCreatedBulkCoupon() {
    this.savingCoupon = false;
    this.showBulkCouponDrawer = false;
    this.deselectAllContacts();
    this.keyBulkCoupon++;
  }

  onSavingCoupon(event) {
    this.savingCoupon = event;
  }

  /**
   * Bulk create Membership
   */
  showMembeshipDrawerSection = false;
  bulkCreateMembership = false;
  formModelMembership: Partial<Membership> = null;
  timestamp = moment().unix();
  selectedContactsMembership = [];

  onBulkMembership() {
    this.bulkCreateMembership = true;
    this.selectedContactsMembership = this.$refs.provider.items.filter(
      item => item['checked'] === true,
    );
    this.formModelMembership = {
      member_ids: this.selectedContactsMembership.map(({ id }) => id),
      price: 0,
      status: 'ongoing',
    };
    this.showMembeshipDrawerSection = true;
  }

  onCloseMembershipModal() {
    this.formModelMembership = null;
    this.showMembeshipDrawerSection = false;
  }

  get formCancelPortalTarget(): string {
    return `membership-form-cancel-portal-target-${this.timestamp}`;
  }

  get formActionsPortalTarget(): string {
    return `membership-form-actions-portal-target-${this.timestamp}`;
  }

  onCloseModal() {
    this.formModelMembership = null;
    this.showMembeshipDrawerSection = false;
  }
  // Bulk soft delete modal (move to trash)
  showBulkMoveToTrashModal = false;
  onBulkSoftDelete() {
    this.showBulkMoveToTrashModal = true;
  }
  onCLoseBulkSoftDelete() {
    this.showBulkMoveToTrashModal = false;
  }
  onSuccessSoftDelete() {
    this.$refs.softDeleteConfirmationModal.onCloseModal();
    this.$refs.provider.getEntities();
  }
  // Here, put the same fields as you put in dataToShowForBulkTrashedEntity in your entity Service
  bulkMoveToTrashDataToShow = [['code'], ['contact']];

  // Bulk restore modal (recover)

  showBulkRecoverModal = false;

  onBulkRecover() {
    this.showBulkRecoverModal = true;
  }

  onCLoseBulkRecover() {
    this.showBulkRecoverModal = false;
  }

  onSuccessBulkRecover() {
    this.$refs.provider.getEntities();
  }

  async downloadCard(id: number | string) {
    const basePath = this.$bpcApiRouteHelper.getEntityPath('contact');
    const relativePath = '/download-card/' + id;

    this.$bpcApi.postDownload(`${basePath + relativePath}`, '').subscribe();
  }

  bulkRecoverDataToShow = [['code'], ['contact']];

  get hasAccessControlModuleActivated() {
    return this.$modulesService.hasModuleActivated(ModulesHelper.ACCESS_CONTROL_MODULE_NAME);
  }

  get canCreateAccessControlBadges() {
    const permissionName = AuthorizationHelper.getPermissionName('store', 'access_control_badges');
    return this.$authorizationService.hasPermissions([permissionName]);
  }

  get initialAccessControlBadgeData(): Partial<AccessControlBadge> {
    return { contact_id: this.selectedRowEntityId, status: 'active', type: 'rfid' };
  }
  isSavingAccessControlBadge: boolean = false;
  labelSaveAccessControlBadgeButton: string =
    'global.access_control_badge.label_to_add_from_contact';

  get selectedRowEntityId() {
    return this.entity?.id;
  }

  get accessControlBadgeFormCancelPortalTarget() {
    return `access-control-badge-form-cancel-action-${this.timestamp}`;
  }

  get accessControlBadgeFormSavePortalTarget() {
    return `access-control-badge-form-actions-${this.timestamp}`;
  }

  get titleAddAccessControlBadgeModal(): string {
    return (
      this.$t('global.modals.create') + ' ' + this.$t('global.models.access-control-badge.name')
    );
  }

  get showDownloadCardAction(): boolean {
    return !!this.entity?.id && this.contacCardEnabled;
  }

  onClickCreateAccessControlBadge() {
    this.$bvModal.show('modal-add-access-control-badge');
  }

  onCloseAccessControlBadgeModal() {
    this.$bvModal.hide('modal-add-access-control-badge');
  }
}
